import React, {
    useMemo,
    useState,
    useEffect
} from "react";

import {
    useNavigate
} from "react-router-dom";

import {
    useCart
} from "../context/CartContext";

import {
    createOrder
} from "../services/OrderService";

import {
    useAuth
} from "../../contexts/AuthContext";


export default function MarketplaceCheckout() {

    const navigate =
        useNavigate();

    const { user } =
        useAuth();

    const {
        items,
        subtotal
    } = useCart();


    const [saving, setSaving] =
        useState(false);

    const [error, setError] =
        useState(null);


    const [form, setForm] =
        useState({

            name:
                user?.name || "",

            email:
                user?.email || "",

            phone:
                "",

            address:
                "",

            city:
                "",

            state:
                "",

            postalCode:
                "",

            country:
                "India",

            notes:
                ""

        });


    /*
    --------------------------------------------------
    CASHFREE SDK
    --------------------------------------------------
    */

    const [cashfree, setCashfree] =
        useState(null);


    useEffect(() => {

        /*
        ----------------------------------------------
        Cashfree JS SDK is loaded only once.
        ----------------------------------------------
        */

        if (
            window.Cashfree
        ) {

            try {

                const instance =
                    window.Cashfree({
                        mode:
                            "sandbox"
                    });

                setCashfree(
                    instance
                );

            }

            catch (err) {

                console.error(
                    "Cashfree initialization failed:",
                    err
                );

            }

            return;

        }


        const existingScript =
            document.querySelector(
                'script[src="https://sdk.cashfree.com/js/v3/cashfree.js"]'
            );


        if (existingScript) {

            existingScript.addEventListener(
                "load",
                () => {

                    try {

                        if (
                            window.Cashfree
                        ) {

                            const instance =
                                window.Cashfree({
                                    mode:
                                        "sandbox"
                                });

                            setCashfree(
                                instance
                            );

                        }

                    }

                    catch (err) {

                        console.error(
                            "Cashfree initialization failed:",
                            err
                        );

                    }

                }
            );

            return;

        }


        const script =
            document.createElement(
                "script"
            );


        script.src =
            "https://sdk.cashfree.com/js/v3/cashfree.js";

        script.async =
            true;


        script.onload =
            () => {

                try {

                    if (
                        window.Cashfree
                    ) {

                        const instance =
                            window.Cashfree({
                                mode:
                                    "sandbox"
                            });

                        setCashfree(
                            instance
                        );

                    }

                }

                catch (err) {

                    console.error(
                        "Cashfree initialization failed:",
                        err
                    );

                }

            };


        script.onerror =
            () => {

                console.error(
                    "Unable to load Cashfree SDK."
                );

                setError(
                    "Unable to load the payment system. Please try again."
                );

            };


        document.body.appendChild(
            script
        );


        return () => {

            /*
            Do not remove the script here.

            Other pages/components may also use
            Cashfree after this component unmounts.
            */

        };

    }, []);


    /*
    --------------------------------------------------
    UPDATE FIELD
    --------------------------------------------------
    */

    const updateField = (
        field,
        value
    ) => {

        setForm(
            previous => ({

                ...previous,

                [field]:
                    value

            })
        );

    };


    /*
    --------------------------------------------------
    ORDER TOTAL
    --------------------------------------------------
    */

    const total =
        useMemo(
            () => subtotal,
            [subtotal]
        );


    /*
    --------------------------------------------------
    CREATE CASHFREE PAYMENT
    --------------------------------------------------
    */

    const createCashfreePayment =
        async (
            orderId
        ) => {

            /*
            ------------------------------------------
            The PocketBase order already exists.

            We now send ONLY the order ID to our
            server.

            The server retrieves the actual order
            from PocketBase and creates the Cashfree
            order using the server-side credentials.
            ------------------------------------------
            */

            const response =
                await fetch(
                    "https://api.myfavv.live/payments/create",
                    {

                        method:
                            "POST",

                        headers: {

                            "Content-Type":
                                "application/json"

                        },

                        credentials:
                            "include",

                        body:
                            JSON.stringify({

                                orderId:
                                    orderId

                            })

                    }
                );


            let data = null;


            try {

                data =
                    await response.json();

            }

            catch {

                data = null;

            }


            if (
                !response.ok
            ) {

                throw new Error(
                    data?.error ||
                    "Unable to create the Cashfree payment."
                );

            }


            if (
                !data?.paymentSessionId
            ) {

                throw new Error(
                    "Cashfree did not return a payment session."
                );

            }


            return data;

        };


    /*
    --------------------------------------------------
    OPEN CASHFREE CHECKOUT
    --------------------------------------------------
    */

    const openCashfreeCheckout =
        async (
            paymentSessionId
        ) => {

            if (
                !paymentSessionId
            ) {

                throw new Error(
                    "Payment session ID is missing."
                );

            }


            /*
            ------------------------------------------
            Make sure the Cashfree SDK is available.
            ------------------------------------------
            */

            let cashfreeInstance =
                cashfree;


            if (
                !cashfreeInstance &&
                window.Cashfree
            ) {

                cashfreeInstance =
                    window.Cashfree({
                        mode:
                            "sandbox"
                    });

                setCashfree(
                    cashfreeInstance
                );

            }


            if (
                !cashfreeInstance
            ) {

                throw new Error(
                    "Payment system is still loading. Please try again."
                );

            }


            /*
            ------------------------------------------
            Open Cashfree hosted checkout.

            IMPORTANT:

            This does NOT mark the PocketBase order
            as paid.

            Payment verification will happen on the
            backend later.
            ------------------------------------------
            */

            const checkoutOptions = {

                paymentSessionId:
                    paymentSessionId,

                redirectTarget:
                    "_self"

            };


            const result =
                await cashfreeInstance
                    .checkout(
                        checkoutOptions
                    );


            /*
            ------------------------------------------
            Cashfree checkout errors are handled here.

            A frontend checkout result is NOT treated
            as proof of payment.
            ------------------------------------------
            */

            if (
                result?.error
            ) {

                console.error(
                    "Cashfree checkout error:",
                    result.error
                );

                throw new Error(
                    result.error?.message ||
                    "Cashfree checkout could not be opened."
                );

            }

        };


    /*
    --------------------------------------------------
    PLACE ORDER
    --------------------------------------------------
    */

    const handlePlaceOrder =
        async () => {

            if (!user?.id) {

                setError(
                    "Please log in before placing an order."
                );

                return;

            }


            if (!items.length) {

                setError(
                    "Your cart is empty."
                );

                return;

            }


            if (!form.name.trim()) {

                setError(
                    "Please enter your name."
                );

                return;

            }


            if (!form.email.trim()) {

                setError(
                    "Please enter your email."
                );

                return;

            }


            if (!form.phone.trim()) {

                setError(
                    "Please enter your phone number."
                );

                return;

            }


            if (!form.address.trim()) {

                setError(
                    "Please enter your shipping address."
                );

                return;

            }


            try {

                setSaving(true);

                setError(null);


                /*
                ------------------------------------------
                Current implementation supports one brand
                per order.
                ------------------------------------------
                */

                const brandIds =
                    [
                        ...new Set(
                            items
                                .map(
                                    item =>
                                        item.brandId
                                )
                                .filter(Boolean)
                        )
                    ];


                if (
                    brandIds.length !== 1
                ) {

                    throw new Error(
                        "Cart currently supports products from one brand per order."
                    );

                }


                /*
                ------------------------------------------
                ORDER ITEM SNAPSHOT
                ------------------------------------------
                */

                const orderItems =
                    items.map(
                        item => ({

                            productId:
                                item.productId,

                            variantId:
                                item.variantId,

                            title:
                                item.title,

                            quantity:
                                Number(
                                    item.quantity
                                ),

                            unitPrice:
                                Number(
                                    item.unitPrice
                                ),

                            currency:
                                item.currency,

                            image:
                                item.image,

                            sku:
                                item.sku,

                            subtotal:
                                Number(
                                    item.unitPrice
                                ) *
                                Number(
                                    item.quantity
                                ),

                            variant:
                                item.variant

                        })
                    );


                /*
                ------------------------------------------
                CREATE PENDING ORDER
                ------------------------------------------
                */

                const order =
                    await createOrder({

                        buyer:
                            user.id,

                        brand:
                            brandIds[0],

                        /*
                        Order is created first.
                        It has NOT been paid yet.
                        */

                        status:
                            "pending",

                        paymentStatus:
                            "pending",

                        fulfillmentStatus:
                            "unfulfilled",

                        /*
                        Inventory must not be deducted
                        at this stage.
                        */

                        currency:
                            items[0]?.currency ||
                            "INR",

                        subtotal:
                            Number(
                                subtotal
                            ),

                        shippingTotal:
                            0,

                        taxTotal:
                            0,

                        discountTotal:
                            0,

                        grandTotal:
                            Number(
                                total
                            ),

                        customerName:
                            form.name,

                        customerEmail:
                            form.email,

                        customerPhone:
                            form.phone,

                        shippingAddress: {

                            address:
                                form.address,

                            city:
                                form.city,

                            state:
                                form.state,

                            postalCode:
                                form.postalCode,

                            country:
                                form.country

                        },

                        billingAddress: {

                            address:
                                form.address,

                            city:
                                form.city,

                            state:
                                form.state,

                            postalCode:
                                form.postalCode,

                            country:
                                form.country

                        },

                        notes:
                            form.notes,

                        items:
                            orderItems

                    });


                /*
                ------------------------------------------
                IMPORTANT

                The PocketBase order is still:

                    status = pending
                    paymentStatus = pending

                Do NOT clear the cart.

                Do NOT mark the order as paid.

                Do NOT deduct inventory.

                ------------------------------------------
                */


                /*
                ------------------------------------------
                CREATE CASHFREE PAYMENT ORDER
                ------------------------------------------
                */

                const payment =
                    await createCashfreePayment(
                        order.id
                    );


                console.log(
                    "? Cashfree payment session created:",
                    {
                        orderId:
                            order.id,

                        cashfreeOrderId:
                            payment.cashfreeOrderId
                    }
                );


                /*
                ------------------------------------------
                OPEN CASHFREE CHECKOUT
                ------------------------------------------
                */

                await openCashfreeCheckout(
                    payment.paymentSessionId
                );


                /*
                ------------------------------------------
                IMPORTANT

                Nothing below this point marks the
                order as paid.

                Cashfree's frontend response/callback
                is NOT trusted as proof of payment.

                The backend must verify the payment
                with Cashfree and only then update:

                    paymentStatus = "paid"
                    status = "paid"
                    paymentId = verifiedPaymentId
                    paidAt = timestamp

                Inventory deduction will also happen
                only after that verified payment.
                ------------------------------------------
                */

            }

            catch (err) {

                console.error(
                    "Payment/order creation failed:",
                    err
                );


                setError(
                    err?.message ||
                    "Unable to start the payment."
                );

            }

            finally {

                setSaving(false);

            }

        };


    /*
    --------------------------------------------------
    EMPTY
    --------------------------------------------------
    */

    if (!items.length) {

        return (

            <div
                style={{
                    background: "#fff",
                    borderRadius: 18,
                    padding: 50,
                    textAlign: "center"
                }}
            >

                <h2>
                    Your cart is empty
                </h2>

                <button
                    onClick={() =>
                        navigate(
                            "/brand/marketplace"
                        )
                    }
                    style={{
                        marginTop: 15,
                        padding:
                            "12px 22px",
                        border: "none",
                        borderRadius: 9,
                        background:
                            "#2563eb",
                        color: "#fff",
                        cursor: "pointer",
                        fontWeight: 600
                    }}
                >
                    Continue Shopping
                </button>

            </div>

        );

    }


    return (

        <div
            style={{
                maxWidth: 1100,
                margin: "0 auto",
                paddingBottom: 50
            }}
        >

            <button
                onClick={() =>
                    navigate(
                        "/brand/cart"
                    )
                }
                style={{
                    border: "none",
                    background:
                        "transparent",
                    color: "#2563eb",
                    cursor: "pointer",
                    fontWeight: 600,
                    padding: 0,
                    marginBottom: 25
                }}
            >
                ? Back to Cart
            </button>


            <h1>
                Checkout
            </h1>


            {error && (

                <div
                    style={{
                        background:
                            "#fef2f2",
                        border:
                            "1px solid #fecaca",
                        color:
                            "#991b1b",
                        padding: 15,
                        borderRadius: 10,
                        marginBottom: 20
                    }}
                >
                    {error}
                </div>

            )}


            <div
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        "minmax(0,1fr) 350px",
                    gap: 25,
                    alignItems:
                        "start"
                }}
            >

                {/* FORM */}

                <div
                    style={{
                        background:
                            "#fff",
                        borderRadius: 18,
                        padding: 28
                    }}
                >

                    <h2>
                        Delivery Information
                    </h2>


                    {[
                        [
                            "name",
                            "Full Name"
                        ],
                        [
                            "email",
                            "Email"
                        ],
                        [
                            "phone",
                            "Phone"
                        ],
                        [
                            "address",
                            "Address"
                        ],
                        [
                            "city",
                            "City"
                        ],
                        [
                            "state",
                            "State"
                        ],
                        [
                            "postalCode",
                            "Postal Code"
                        ],
                        [
                            "country",
                            "Country"
                        ]

                    ].map(
                        field => (

                            <div
                                key={
                                    field[0]
                                }
                                style={{
                                    marginTop: 15
                                }}
                            >

                                <label
                                    style={{
                                        display:
                                            "block",
                                        fontWeight:
                                            600,
                                        marginBottom:
                                            7
                                    }}
                                >
                                    {field[1]}
                                </label>

                                <input
                                    value={
                                        form[
                                            field[0]
                                        ]
                                    }
                                    onChange={
                                        e =>
                                            updateField(
                                                field[0],
                                                e.target.value
                                            )
                                    }
                                    style={{
                                        width:
                                            "100%",
                                        boxSizing:
                                            "border-box",
                                        padding:
                                            "12px",
                                        border:
                                            "1px solid #ddd",
                                        borderRadius:
                                            9
                                    }}
                                />

                            </div>

                        )
                    )}


                    <div
                        style={{
                            marginTop: 15
                        }}
                    >

                        <label
                            style={{
                                display:
                                    "block",
                                fontWeight:
                                    600,
                                marginBottom:
                                    7
                            }}
                        >
                            Order Notes
                        </label>

                        <textarea
                            rows={4}
                            value={
                                form.notes
                            }
                            onChange={
                                e =>
                                    updateField(
                                        "notes",
                                        e.target.value
                                    )
                            }
                            style={{
                                width:
                                    "100%",
                                boxSizing:
                                    "border-box",
                                padding:
                                    "12px",
                                border:
                                    "1px solid #ddd",
                                borderRadius:
                                    9,
                                resize:
                                    "vertical"
                            }}
                        />

                    </div>

                </div>


                {/* SUMMARY */}

                <div
                    style={{
                        background:
                            "#fff",
                        borderRadius: 18,
                        padding: 25,
                        position:
                            "sticky",
                        top: 20
                    }}
                >

                    <h2>
                        Order Summary
                    </h2>


                    {items.map(
                        item => (

                            <div
                                key={
                                    item.cartKey
                                }
                                style={{
                                    padding:
                                        "15px 0",
                                    borderBottom:
                                        "1px solid #eee"
                                }}
                            >

                                <strong>
                                    {item.title}
                                </strong>

                                <div
                                    style={{
                                        marginTop: 5,
                                        color:
                                            "#64748b",
                                        fontSize:
                                            13
                                    }}
                                >
                                    {item.quantity}
                                    {" � "}
                                    {item.unitPrice}
                                </div>

                            </div>

                        )
                    )}


                    <div
                        style={{
                            display:
                                "flex",
                            justifyContent:
                                "space-between",
                            marginTop: 20,
                            fontSize: 20
                        }}
                    >

                        <strong>
                            Total
                        </strong>

                        <strong>
                            ?
                            {Number(
                                total
                            ).toFixed(2)}
                        </strong>

                    </div>


                    <button
                        onClick={
                            handlePlaceOrder
                        }
                        disabled={
                            saving
                        }
                        style={{
                            width:
                                "100%",
                            marginTop:
                                20,
                            padding:
                                "14px",
                            border:
                                "none",
                            borderRadius:
                                10,
                            background:
                                "rgb(255,0,204)",
                            color:
                                "#fff",
                            cursor:
                                saving
                                    ? "default"
                                    : "pointer",
                            fontWeight:
                                700,
                            opacity:
                                saving
                                    ? .7
                                    : 1
                        }}
                    >
                        {saving
                            ? "Creating Payment..."
                            : "Place Order"}

                    </button>

                </div>

            </div>

        </div>

    );

}